import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { pagePaths } from "../../constants/pagePaths";
import { ChangePasswordPage } from "../../pages/Authentication/ChangePasswordPage";
import { ResetPasswordPage } from "../../pages/Authentication/ResetPasswordPage";
import { SignInPage } from "../../pages/Authentication/SignInPage";
import { SignUpPage } from "../../pages/Authentication/SignUpPage";
import { UpdatePasswordPage } from "../../pages/Authentication/UpdatePasswordPage";
import { VerifyEmailPage } from "../../pages/Authentication/VerifyEmailPage";
import { UserNotFoundPage } from "../../pages/Authentication/UserNotFoundPage";
import { getFederationErrorDescription, clearFederationErrorDescription } from "../../federationErrorHandler";
import { buildFederatedSignInRetryUrl, buildUserDisabledUrl, buildUserNotFoundUrl } from "../../utils/BuildURLs";
import { UserDisabledPage } from "../../pages/Authentication/UserDisabledPage";
import { PrivacyPolicyPage } from "../../pages/PrivacyPolicy";
import { HomeLoginPage } from "../../pages/HomeLoginPage";
import type { FederationSignUpResult } from "./types";
import { FederationSignUpResponse } from "./types";

export const UnAuthenticatedApplication = () => {
  const navigate = useNavigate();

  const getFederationSignUpResponse = (): FederationSignUpResult => {
    const signUpResponse = getFederationErrorDescription();
    if (signUpResponse) {
      if (signUpResponse.includes(FederationSignUpResponse.USER_FOUND)) {
        return {
          response: FederationSignUpResponse.USER_FOUND,
          email: "",
        };
      }
      if (signUpResponse.includes(FederationSignUpResponse.USER_NOT_FOUND)) {
        const parts = signUpResponse.split(FederationSignUpResponse.USER_NOT_FOUND + "_");
        return {
          response: FederationSignUpResponse.USER_NOT_FOUND,
          email: parts.length > 1 ? parts[1].split(". ")[0] : "",
        };
      } else if (signUpResponse.includes(FederationSignUpResponse.USER_DISABLED)) {
        const parts = signUpResponse.split(FederationSignUpResponse.USER_DISABLED + "_");
        return {
          response: FederationSignUpResponse.USER_DISABLED,
          email: parts.length > 1 ? parts[1].split(". ")[0] : "",
        };
      } else if (signUpResponse.includes(FederationSignUpResponse.USER_DISABLED_AFTER)) {
        return {
          response: FederationSignUpResponse.USER_DISABLED_AFTER,
          email: "", // OAuth flow does not provide this information
        };
      }
    }
    return {
      response: FederationSignUpResponse.RESPONSE_NOT_PARSED,
      email: "",
    };
  };

  useEffect(() => {
    const { response, email } = getFederationSignUpResponse();
    clearFederationErrorDescription();
    if (response === FederationSignUpResponse.USER_FOUND) {
      navigate(buildFederatedSignInRetryUrl("google"), { replace: true });
    }
    if (response === FederationSignUpResponse.USER_NOT_FOUND) {
      navigate(buildUserNotFoundUrl(email), { replace: true });
    } else if (response === FederationSignUpResponse.USER_DISABLED) {
      navigate(buildUserDisabledUrl(email), { replace: true });
    } else if (response === FederationSignUpResponse.USER_DISABLED_AFTER) {
      navigate(buildUserDisabledUrl(undefined), { replace: true });
    }
  }, [navigate]);

  return (
    <Routes>
      <Route path={pagePaths.authentication}>
        <Route path={pagePaths.signUp} element={<SignUpPage />} />
        <Route path={pagePaths.verifyEmailAddress} element={<VerifyEmailPage />} />
        <Route path={pagePaths.signIn} element={<SignInPage />} />
        <Route path={pagePaths.resetPassword} element={<ResetPasswordPage />} />
        <Route path={pagePaths.changePassword} element={<ChangePasswordPage />} />
        <Route path={pagePaths.updatePassword} element={<UpdatePasswordPage />} />
      </Route>
      <Route path={pagePaths.homeLogin} element={<HomeLoginPage />} />
      <Route path={pagePaths.distributorSignIn} element={<SignInPage />} />
      <Route path={pagePaths.userNotFound} element={<UserNotFoundPage />} />
      <Route path={pagePaths.userDisabled} element={<UserDisabledPage />} />
      <Route path={pagePaths.privacyPolicy} element={<PrivacyPolicyPage />} />
      <Route
        path="*"
        element={
          <Navigate
            to={{
              pathname: pagePaths.homeLogin,
              search: location.search,
            }}
            replace
          />
        }
      />
    </Routes>
  );
};
